import React, { FC, Suspense, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import type { addProfanityRulePanel_AssignProductMutation } from './__generated__/addProfanityRulePanel_AssignProductMutation.graphql';
import type { addProfanityRulePanel_ProfanityFilterRulesListQuery } from './__generated__/addProfanityRulePanel_ProfanityFilterRulesListQuery.graphql';
import {
    EmptyState,
    FooterButton,
    ItemActionSuccess,
    ItemDot,
    ItemName,
    ItemNameAction,
    PanelItem,
    PanelItemSpaced,
    StackedBody,
    StackedFooterButtons
} from '../common/styled';

const AddProfanityRulePanelContent: FC<{
    productId: number;
    productRelayId: string;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew: () => void;
}> = ({ productId, productRelayId, existingRuleIds, onBack, onAssigned, onCreateNew }) => {
    const [assigning, setAssigning] = useState<string | null>(null);

    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addProfanityRulePanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productRelayId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addProfanityRulePanel_ProfanityFilterRulesListQuery>(
        graphql`
            query addProfanityRulePanel_ProfanityFilterRulesListQuery {
                profanityFilterRules(first: 200) {
                    nodes {
                        id
                        name
                    }
                }
            }
        `,
        {}
    );

    const allRules = React.useMemo(() => {
        const nodes = data?.profanityFilterRules?.nodes || [];
        return nodes.map((node) => ({ id: node!.id, name: node!.name || node!.id }));
    }, [data]);

    const [commitAssign] = useMutation<addProfanityRulePanel_AssignProductMutation>(graphql`
        mutation addProfanityRulePanel_AssignProductMutation($profanityRuleId: ID!, $productIds: [Long!]) {
            assignProductsToProfanityRule(profanityRuleId: $profanityRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const handleAssign = (rule: { id: string; name: string }) => {
        setAssigning(rule.id);
        commitAssign({
            variables: {
                profanityRuleId: rule.id,
                productIds: [productId]
            },
            onCompleted: () => {
                setAssigning(null);
                onAssigned();
            },
            onError: () => {
                setAssigning(null);
            }
        });
    };

    const availableRules = allRules.filter((rule) => !existingRuleIds.includes(rule.id));

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add profanity filter rule', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableRules.length === 0 && <EmptyState>{T._('All profanity filter rules are already assigned', 'Admin')}</EmptyState>}
                {availableRules.map((rule) => (
                    <PanelItem key={rule.id} $clickable={assigning === null} onClick={() => assigning === null && handleAssign(rule)}>
                        <ItemDot $color='#3b82f6' />
                        <ItemName>{rule.name}</ItemName>
                        <ItemActionSuccess>{assigning === rule.id ? T._('Assigning...', 'Admin') : T._('Assign', 'Admin')}</ItemActionSuccess>
                    </PanelItem>
                ))}
                <PanelItemSpaced
                    $clickable
                    onClick={() => {
                        onBack();
                        onCreateNew();
                    }}
                >
                    <ItemDot $color='#d1d5db' />
                    <ItemNameAction>+ {T._('Create new', 'Admin')}</ItemNameAction>
                </PanelItemSpaced>
            </StackedBody>
        </Drawer>
    );
};

export const AddProfanityRulePanel: FC<{
    productId: number;
    productRelayId: string;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew: () => void;
}> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add profanity filter rule', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </Drawer>
            }
        >
            <AddProfanityRulePanelContent {...props} />
        </Suspense>
    );
};
